import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import authReducer from "features/authSlice";
import { applicationApi } from "pages/application/applicationService";
import questionReducer from "pages/application/question/questionSlice";
import { authenticationApi } from "pages/authentication/authenticationService";
import { userApi } from "pages/authentication/userService";
import dashboardReducer from "pages/dashboard/store/dashboardSlice";
import { dashboardApi } from "pages/dashboard/home/dashboardService";
import { dashboardLogoutApi } from "pages/dashboard/home/dashboardLogoutService";
import { forgotPasswordApi } from "pages/forgot-password/forgotPasswordService";
import { loginApi } from "pages/login/loginService";
import loginReducer from "pages/login/loginSlice";
import { mfaApi } from "pages/MFA/mfaService";
import { verificationApi } from "pages/online-verification/verificationService";
import { recoverApi } from "pages/recover-password/recoverPasswordService";
import { registrationApi } from "pages/registration/registrationService";
import { surveyApi } from "pages/survey/surveyService";
import { versionApi } from "pages/version/versionService";
import { welcomePathApi } from "pages/welcome/welcomePathService";
import { persistReducer } from "redux-persist";
import storageSession from "redux-persist/lib/storage/session";
import { complianceApi } from "services/common/complianceService";
import { featureFlagApi } from "services/common/featureFlagService";
import { logOutApi } from "pages/login/logoutService";
import { afterLogoutApi } from "pages/login/afterLogoutService";
import { listenerMiddleware } from "./listners/logoutListners";
import { impersonateApi } from "pages/impersonate/impersonateService";
import resendCooldownReducer from "features/resendCooldownSlice";
import  loanOfficerReducer  from "services/common/loanOfficerSlice";

const persistConfig = {
  key: "root",
  storage: storageSession,
  whitelist: ["auth", "login", "dashboard", "loanOfficer", "resendCooldown"]
};
const reducer = combineReducers({
  auth: authReducer,
  question: questionReducer,
  login: loginReducer,
  dashboard: dashboardReducer,
  resendCooldown: resendCooldownReducer,
  loanOfficer: loanOfficerReducer,
  [versionApi.reducerPath]: versionApi.reducer,
  [featureFlagApi.reducerPath]: featureFlagApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [dashboardLogoutApi.reducerPath]: dashboardLogoutApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [verificationApi.reducerPath]: verificationApi.reducer,
  [welcomePathApi.reducerPath]: welcomePathApi.reducer,
  [registrationApi.reducerPath]: registrationApi.reducer,
  [loginApi.reducerPath]: loginApi.reducer,
  [impersonateApi.reducerPath]: impersonateApi.reducer,
  [forgotPasswordApi.reducerPath]: forgotPasswordApi.reducer,
  [mfaApi.reducerPath]: mfaApi.reducer,
  [authenticationApi.reducerPath]: authenticationApi.reducer,
  [recoverApi.reducerPath]: recoverApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [complianceApi.reducerPath]: complianceApi.reducer,
  [logOutApi.reducerPath]: logOutApi.reducer,
  [afterLogoutApi.reducerPath]: afterLogoutApi.reducer,
  [surveyApi.reducerPath]: surveyApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, reducer);
export const store = configureStore({
  reducer: persistedReducer,
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat([
      applicationApi.middleware,
      dashboardApi.middleware,
      dashboardLogoutApi.middleware,
      verificationApi.middleware,
      welcomePathApi.middleware,
      registrationApi.middleware,
      loginApi.middleware,
      impersonateApi.middleware,
      versionApi.middleware,
      mfaApi.middleware,
      authenticationApi.middleware,
      forgotPasswordApi.middleware,
      recoverApi.middleware,
      userApi.middleware,
      featureFlagApi.middleware,
      complianceApi.middleware,
      logOutApi.middleware,
      afterLogoutApi.middleware,
      surveyApi.middleware,
      listenerMiddleware.middleware
    ]),
});
setupListeners(store.dispatch);
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;